import React, { createContext, useEffect, useRef, useState, useCallback, useContext, useMemo } from 'react';
import { useAtom } from 'jotai';
import MediaCore from '../../api/MediaCore';
import { generateConstraints, isChrome } from '../../api/MediaCore/lib';
import { permissionsAtom } from '../../store/media';
import DispatchContext from '../../contexts/DispatchContext';
import StateContext from '../../contexts/StateContext';
import { useLogs } from '../../hooks';
import useFeatureFlags from '@app/feature-flags';
import useStudioTracking from '@app/hooks/useStudioTracking.js';
import analytics from '@app/utils/segment.js';
import { useDebounce } from 'react-use';
import isEqual from 'lodash/isEqual';

export const MediaCoreContext = createContext();

export const MediaCoreProvider = ({ children }) => {
	const logger = useLogs();
	const { currentParticipant, studioSettings, streamGenerating, error, recording, sessionTerminated } =
		useContext(StateContext);
	const { updateTrackingState, getTrackingData, wrapDefaults } = useStudioTracking();
	const errorRef = useRef(error);
	const dispatch = useContext(DispatchContext);
	const mediaCore = useRef(null);
	const requestingPermissions = useRef(false);
	const featureFlags = useFeatureFlags();
	const [mediaCoreReady, setMediaCoreReady] = useState(false);
	const [permissions, setPermissions] = useState({ camera: false, microphone: false });
	const [atomPermissions, setAtomPermissions] = useAtom(permissionsAtom);
	const [devices, setDevices] = useState([]);
	const [activeDevices, setActiveDevices] = useState({ audioinput: null, audiooutput: null, videoinput: null });
	const [mediaStream, setMediaStream] = useState(null);
	const [streamMuted, setStreamMuted] = useState({ audio: false, video: false });

	useEffect(() => {
		if (devices.length) {
			const devicesData = devices.map((device) => ({
				kind: device.kind,
				deviceId: device.deviceId,
				label: device.label,
			}));
			dispatch({
				type: 'updateCurrentParticipant',
				payload: { devices: devicesData },
			});
		}
	}, [devices]);

	useEffect(() => {
		mediaCore.current && mediaCore.current.setMetadata({ currentParticipant });
	}, [currentParticipant]);

	const frameRate = useMemo(() => {
		let result = studioSettings.frameRate;
		if (featureFlags.frameRateVariation) {
			result = 24;
		}
		return result;
	}, [studioSettings.frameRate]);

	const handlePermissionsChanged = (ev) => {
		setPermissions((prev) => Object.assign({}, prev, ev));
	};

	const handleDevicesChanged = (devices) => {
		logger.logDeviceList({ devices });
		const filteredDevices = devices.filter((d) => !(d.deviceId === 'default' && d.kind === 'audioinput'));
		setDevices(filteredDevices);
	};

	const handleActiveAudioDeviceChanged = (device) => {
		setActiveDevices((prev) => ({ ...prev, audioinput: device }));

		try {
			if (device) {
				const mediaDevice = {
					mediaDeviceLabel: device.label,
					mediaDeviceType: device.kind,
					mediaDeviceId: device.deviceId,
					mediaDeviceGroupId: device.groupId,
					mediaDeviceMuted: mediaCore.current.mediaStream.isAudioMuted,
					mediaDeviceEnabled: mediaCore.current.mediaStream.audioTrack?.enabled,
				};

				if (!isEqual(getTrackingData('devices', 'activeAudioInput'), mediaDevice)) {
					trackDeviceSet(mediaDevice);
				}

				updateTrackingState({
					devices: {
						activeAudioInput: mediaDevice,
					},
				});
			}
		} catch (err) {
			console.warn('[tracking] Error updating tracking data.', err);
		}
	};

	const handleActiveVideoDeviceChanged = (device) => {
		setActiveDevices((prev) => ({ ...prev, videoinput: device }));

		try {
			if (device) {
				const mediaDevice = {
					mediaDeviceLabel: device.label,
					mediaDeviceType: device.kind,
					mediaDeviceId: device.deviceId,
					mediaDeviceGroupId: device.groupId,
					mediaDeviceMuted: mediaCore.current.mediaStream?.isVideoMuted,
					mediaDeviceEnabled: mediaCore.current.mediaStream.videoTrack?.enabled,
				};

				if (!isEqual(getTrackingData('devices', 'activeVideoInput'), mediaDevice)) {
					trackDeviceSet(mediaDevice);
				}

				updateTrackingState({
					devices: {
						activeVideoInput: mediaDevice,
					},
				});
			}
		} catch (err) {
			console.warn('[tracking] Error updating tracking data.', err);
		}
	};

	const handleMuteChanged = (type, muted) => {
		setStreamMuted((prev) => ({ ...prev, [type]: muted }));

		try {
			updateTrackingState({
				devices: {
					[type === 'video' ? 'activeVideoInput' : 'activeAudioInput']: {
						mediaDeviceMuted:
							type === 'video'
								? mediaCore.current.mediaStream?.isVideoMuted
								: mediaCore.current.mediaStream?.isAudioMuted,
					},
				},
			});
		} catch (err) {
			console.warn('[tracking] Error updating tracking data.', err);
		}
	};

	const handleActiveOutputDeviceChanged = (device) => {
		setActiveDevices((prev) => ({ ...prev, audiooutput: device }));
		dispatch({
			type: 'setActiveAudioOutput',
			payload: device._device,
		});

		try {
			if (device) {
				const mediaDevice = {
					mediaDeviceLabel: device.label,
					mediaDeviceType: device.kind,
					mediaDeviceId: device.deviceId,
					mediaDeviceGroupId: device.groupId,
					mediaDeviceMuted: false,
					mediaDeviceEnabled: false,
				};

				if (!isEqual(getTrackingData('devices', 'activeAudioOutput'), mediaDevice)) {
					trackDeviceSet(mediaDevice);
				}

				updateTrackingState({
					devices: {
						activeAudioOutput: mediaDevice,
					},
				});
			}
		} catch (err) {
			console.warn('[tracking] Error updating tracking data.', err);
		}
	};

	const trackDeviceDisconnected = (devices) => {
		try {
			devices.forEach((device) => {
				analytics.track(
					'deviceDisconnected',
					wrapDefaults({
						mediaDevice: {
							mediaDeviceLabel: device.label,
							mediaDeviceType: device.kind,
							mediaDeviceId: device.deviceId,
							mediaDeviceGroupId: device.groupId,
						},
						liveCallId: getTrackingData('studio', 'liveCallId'),
						studioRecordingStatus: getTrackingData('studio', 'recordingStatus'),
						sessionId: getTrackingData('studio', 'sessionId'),
						recordingId: getTrackingData('studio', 'recordingId'),
						streamId: getTrackingData('studio', 'streamId'),
						studioId: getTrackingData('studio', 'studioId'),
						archiveId: getTrackingData('studio', 'streamId'),
					})
				);
			});
		} catch (error) {
			console.warn('Analytics error', null, { err: error });
		}
	};

	const trackDeviceConnected = (devices) => {
		try {
			devices.forEach((device) => {
				analytics.track(
					'newDeviceConnected',
					wrapDefaults({
						mediaDevice: {
							mediaDeviceLabel: device.label,
							mediaDeviceType: device.kind,
							mediaDeviceId: device.deviceId,
							mediaDeviceGroupId: device.groupId,
						},
						liveCallId: getTrackingData('studio', 'liveCallId'),
						studioId: getTrackingData('studio', 'studioId'),
						studioRecordingStatus: getTrackingData('studio', 'recordingStatus'),
						sessionId: getTrackingData('studio', 'sessionId'),
						recordingId: getTrackingData('studio', 'recordingId'),
					})
				);
			});
		} catch (error) {
			console.warn('Analytics error', null, { err: error });
		}
	};

	const trackDeviceSet = (mediaDevice) => {
		try {
			if (mediaCore.current?.mediaStream) {
				analytics.track(
					'newActiveDeviceSet',
					wrapDefaults({
						mediaDevice,
						studioRecordingStatus: getTrackingData('studio', 'recordingStatus'),
						liveCallId: getTrackingData('studio', 'liveCallId'),
						sessionId: getTrackingData('studio', 'sessionId'),
						recordingId: getTrackingData('studio', 'recordingId'),
						studioId: getTrackingData('studio', 'studioId'),
						mediaStreamConstraintsRequested: mediaCore.current.getConstraints(),
						activeMediaStreamProperties: {
							audio: mediaCore.current?.mediaStream.getAudioDeviceDetails(),
							video: mediaCore.current?.mediaStream.getVideoDeviceDetails(),
						},
					})
				);
			}
		} catch (error) {
			console.error('[newActiveDeviceSet] Analytics error', null, { err: error });
		}
	};

	const trackInitialDevices = () => {
		try {
			analytics.track(
				'initialDevicesConnected',
				wrapDefaults({
					connectedMediaDevicesList: mediaCore.current?._devices.getDevices().map((device) => ({
						mediaDeviceLabel: device.label,
						mediaDeviceType: device.kind,
						mediaDeviceId: device.deviceId,
						mediaDeviceGroupId: device.groupId,
					})),
					liveCallId: getTrackingData('studio', 'liveCallId'),
					studioRole: getTrackingData('studio', 'role'),
					studioId: getTrackingData('studio', 'studioId'),
				})
			);
		} catch (error) {
			console.warn('Analytics error', null, { err: error });
		}
	};

	const updateParticipantActiveDevices = () => {
		const devices = mediaCore.current?.activeDevices; // we use this to make sure we have the latest;
		if (devices) {
			let serializedDevices = {
				video: devices.videoinput || null,
				audio: devices.audioinput || null,
				output: devices.audiooutput || null,
			};

			dispatch({ type: 'setActiveDevices', payload: serializedDevices });
		}
	};

	useEffect(() => {
		updateParticipantActiveDevices();
	}, [activeDevices]);

	useEffect(() => {
		if (sessionTerminated && (recording.status === 'stopped' || recording.status === 'idle')) {
			setTimeout(() => {
				mediaCore.current.stopMediaStream();
			}, 5000);
		}
	}, [sessionTerminated, recording.status]);

	const setActiveDevice = ({ deviceId, kind }) => {
		mediaCore.current.selectDevice(deviceId, kind);

		if (kind === 'audioinput') {
			dispatch({ type: 'toggleAudio', payload: deviceId !== 'mic_off' });
		} else if (kind === 'videoinput') {
			dispatch({ type: 'toggleVideo', payload: deviceId !== 'cam_off' });
		}
	};

	const handleMediaStreamSet = (localMediaStream) => {
		if (currentParticipant.inGreenRoom) {
			// Got stream but there is no video output device. we allow audioOnly.
			if (!mediaCore.current._devices.getDevices('videoinput').length) {
				setAtomPermissions((perm) => ({ ...perm, audioOnly: true }));
			}
		}

		if (localMediaStream?.audioTrack && localMediaStream?.audioTrack?.label?.toUpperCase().includes('RODECASTER')) {
			dispatch({
				type: 'updateCurrentParticipant',
				payload: { alerts: ['RODECasterUse'] },
			});
		}

		// If we get the working mediaStream we should just remove the error alerts.
		if ((localMediaStream?.hasAudio || localMediaStream?.hasVideo) && errorRef.current.type === 'permissionDenied') {
			dispatch({
				type: 'removeError',
			});
		}

		setMediaStream(localMediaStream?.mediaStream || null);
		if (localMediaStream) {
			setStreamMuted({
				audio: localMediaStream.isAudioMuted,
				video: localMediaStream.isVideoMuted,
			});
		}

		updateTrackingState({
			devices: { constraints: mediaCore.current.getConstraints() },
			studio: { streamId: localMediaStream?.id || null },
		});

		updateParticipantActiveDevices();
	};

	const handleStreamGenerating = useCallback((generating) => {
		dispatch({ type: 'setStreamGenerating', payload: generating });
	}, []);

	const handleMediaError = (error) => {
		dispatch(error);
	};

	const handleAudioSuspendedError = (error) => {
		dispatch({
			type: 'setError',
			payload: {
				type: 'audioSuspended',
			},
		});
	};

	useEffect(() => {
		setAtomPermissions((prev) => {
			return Object.assign({}, prev, { camera: permissions.camera, microphone: permissions.microphone });
		});
		updateTrackingState({ permissions: { camera: permissions.camera, microphone: permissions.microphone } });
	}, [permissions.camera, permissions.microphone]);

	useDebounce(
		() => {
			if (permissions.camera || permissions.microphone) {
				trackInitialDevices();
			}
		},
		2000,
		[permissions.camera, permissions.microphone]
	);

	const updateUserMedia = async (reason = '') => {
		if (mediaCore?.current?.initialized) {
			const constraints = generateConstraints(studioSettings.resolution, {
				echoCancellation: currentParticipant.echoCancellation,
				noiseSuppression: studioSettings.noiseSuppression,
				frameRate: studioSettings.frameRate,
				frameRateRequirement: isChrome && !featureFlags.frameRateVariation ? 'exact' : 'min',
			});

			mediaCore.current.updateUserMedia(constraints, true, reason);
		}
	};

	useEffect(() => {
		updateUserMedia('Resolution changed');
	}, [studioSettings.resolution]);

	useEffect(() => {
		errorRef.current = error;
	}, [error]);

	useEffect(() => {
		updateUserMedia('noise cancellation toggled');
	}, [studioSettings.noiseSuppression]);

	useEffect(() => {
		updateUserMedia('framerate toggled');
	}, [studioSettings.frameRate]);

	useEffect(() => {
		updateUserMedia('Echo cancellation toggled');
	}, [currentParticipant.echoCancellation]);

	useEffect(() => {
		const initializeMediaCore = async () => {
			const mc = mediaCore.current;
			setPermissions(await mc.getPermissions());

			mc.on('stream:set', handleMediaStreamSet);
			mc.on('active:audioinput:changed', handleActiveAudioDeviceChanged);
			mc.on('active:videoinput:changed', handleActiveVideoDeviceChanged);
			mc.on('active:audiooutput:changed', handleActiveOutputDeviceChanged);
			mc.on('device:disconnected', trackDeviceDisconnected);
			mc.on('device:connected', trackDeviceConnected);
			mc.on('media:error', handleMediaError);
			mc.on('stream:track:mute', handleMuteChanged);
			mc.on('stream:generating', () => handleStreamGenerating(true));
			mc.on('stream:set', () => handleStreamGenerating(false));
			mc.on('media:error', () => handleStreamGenerating(false));
			mc.on('audio:suspended', handleAudioSuspendedError);

			mc._permissions.on('permissions:changed', handlePermissionsChanged);
			mc._devices.on('devices:changed', handleDevicesChanged);
		};

		// useEffect can return just a function (and not a promise),
		// so we need to wrap up the function.
		if (mediaCoreReady) {
			initializeMediaCore();
		}
	}, [mediaCore, mediaCoreReady]);

	useEffect(() => {
		const defaultConstraints = generateConstraints(studioSettings.resolution, {
			echoCancellation: currentParticipant.echoCancellation,
			noiseSuppression: studioSettings.noiseSuppression,
			frameRate,
			frameRateRequirement: isChrome && !featureFlags.frameRateVariation ? 'exact' : 'min',
		});

		mediaCore.current = new MediaCore(defaultConstraints, featureFlags);
		setMediaCoreReady(true);
		mediaCore.current.initialize(currentParticipant.role !== 'producer' && currentParticipant.role !== 'audience');
	}, []);

	const onPermissionsRequest = async (enabled, reason) => {
		console.log('[multijoin] Permission Request');
		if (requestingPermissions.current === false) {
			requestingPermissions.current = true;
			const { audio, video } = enabled || { audio: true, video: true };

			const constraints = generateConstraints(studioSettings.resolution, {
				echoCancellation: currentParticipant.echoCancellation,
				noiseSuppression: studioSettings.noiseSuppression,
				frameRate,
				frameRateRequirement: isChrome && !featureFlags.frameRateVariation ? 'exact' : 'min',
			});

			if (!audio) {
				constraints.audio = false;
			}

			if (!video) {
				constraints.video = false;
			}

			dispatch({ type: 'toggleAudio', payload: !!constraints.audio });
			dispatch({ type: 'toggleVideo', payload: !!constraints.video });

			const result = await mediaCore.current.requestUserMedia(constraints, false, reason);
			requestingPermissions.current = false;
			return result;
		}
	};

	const onDeviceChange = (deviceId, kind) => {
		mediaCore.current.selectDevice(deviceId, kind);
	};

	const getPermissions = useCallback(() => {
		return mediaCore.current?.getPermissions();
	}, []);

	const stopMediaStream = useCallback(() => {
		return mediaCore.current?.stopMediaStream();
	}, []);

	return (
		<MediaCoreContext.Provider
			value={{
				mediaCore: mediaCore.current,
				permissions: atomPermissions,
				hasPermission: permissions.camera && permissions.microphone,
				devices,
				activeDevices,
				mediaStream,
				setActiveDevice,
				onPermissionsRequest,
				onDeviceChange,
				streamMuted,
				getPermissions,
				stopMediaStream,
			}}
		>
			{mediaCoreReady && children}
		</MediaCoreContext.Provider>
	);
};
